/*
 * ===============================================================================================================
 *                                Copyright 2025, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Trash } from '@jda/lui-common-icon-library-mui5';
import { MRT_Row, MRT_RowData, MRT_ColumnDef } from 'material-react-table';
import { Grid, Tooltip, IconButton, Typography, TableCellProps } from '@mui/material';
import {
  LuiActionsMenu,
  LuiMaterialTable,
  LuiMaterialTableHeadCellStyles,
  LuiPagination
} from '@jda/lui-common-component-library-mui5';

import { useStyles } from './styles';
import { LABELS, MESSAGES } from './constants';
import SnackbarAlert from '../../shared/SnackbarAlert';
import { AlertDialog } from '../../shared/Dialog/AlertDialog';
import interpolateString from '../../utility/stringInterpolation';
import useWindowDimensions from '../../utility/useWindowDimensions';
import { IErrorMessage } from '../../models/Machines/ClientContracts';
import { ErrorMessage } from '../../shared/ErrorMessage/ErrorMessage';
import { SNACKBAR_ALERT_DEFAULT_PROPS } from '../UserDetails/constant';
import { selectResourcePermissions } from '../../store/auth/authSelector';
import { actions, resources } from '../../shared/CheckPermissions/constants';
import { setSnackBarMessage } from '../../store/snackBar/snackBarActionCreator';
import { getAllUserGroupsAction } from '../../store/userGroups/userGroupsActionCreator';
import { checkResourcePermission } from '../../shared/CheckPermissions/CheckPermission';
import { deleteUserGroupRequest, getAllUserGroupsRequest } from '../../services/userGroups';
import TablePaginationActions from '../../shared/TablePaginationActions/TablePaginationActions';
import {
  selectAllUserGroups,
  selectSearchValue,
  selectUserGroupsPagination
} from '../../store/userGroups/userGroupSelector';

const columns = [
  {
    header: 'Display Name',
    accessorKey: 'displayName',
    muiTableHeadCellProps: ({ column, table }) =>
      ({
        sx: theme => ({
          ...LuiMaterialTableHeadCellStyles({
            column,
            theme,
            table,
            styling: 'standard',
            virtualizationEnabled: false
          }),
          width: '25%'
        })
      } as TableCellProps)
  },
  {
    header: 'Identifier',
    accessorKey: 'identifier',
    muiTableHeadCellProps: ({ column, table }) =>
      ({
        sx: theme => ({
          ...LuiMaterialTableHeadCellStyles({
            column,
            theme,
            table,
            styling: 'standard',
            virtualizationEnabled: false
          }),
          width: '25%'
        })
      } as TableCellProps)
  },
  {
    header: 'Created By',
    accessorKey: 'createdByDisplayName',
    muiTableHeadCellProps: ({ column, table }) =>
      ({
        sx: theme => ({
          ...LuiMaterialTableHeadCellStyles({
            column,
            theme,
            table,
            styling: 'standard',
            virtualizationEnabled: false
          }),
          width: '15%'
        })
      } as TableCellProps)
  },
  {
    header: 'Created On',
    accessorKey: 'createdOn',
    muiTableHeadCellProps: ({ column, table }) =>
      ({
        sx: theme => ({
          ...LuiMaterialTableHeadCellStyles({
            column,
            theme,
            table,
            styling: 'standard',
            virtualizationEnabled: false
          }),
        })
      } as TableCellProps),
    Cell: ({ row }: { row: MRT_Row<MRT_RowData> }) => {
      const createdOnStr = moment(row.original.createdOn);
      const createdOn = row.original.createdOn
        ? `${createdOnStr.format('MMM Do YYYY, h:mm:ss A')} GMT`
        : LABELS.NO_VALUE;
      return (
        <Tooltip
          arrow
          title={createdOn}
          placement="bottom"
          disableHoverListener={!row.original.createdOn}
          disableFocusListener={!row.original.createdOn}
        >
          <Typography variant="body1" color="textPrimary" noWrap>
            {createdOn}
          </Typography>
        </Tooltip>
      );
    }
  }
];

const UserGroupsList = ({ onEdit, refreshOnDelete, extendedToolbar, emptyRowsFallback }: any) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const { height } = useWindowDimensions();

  const selectedSearchValue = useSelector(selectSearchValue);
  const selectAllUserGroupsList = useSelector(selectAllUserGroups);
  const resourcePermissions = useSelector(selectResourcePermissions);
  const selectUserGroupPagination = useSelector(selectUserGroupsPagination);

  const [page, setPage] = useState<any>(1);
  const [rowData, setRowData] = useState<any>();
  const [pageSize, setPageSize] = useState<any>(50);
  const [showLoader, showHideLoader] = useState(false);
  const [isPreviewShown, setPreviewShown] = useState(false);
  const [showDialogLoader, setDialogHideLoader] = useState(false);
  const [tableColumns, setTableColumns] = useState<MRT_ColumnDef<MRT_RowData>[]>(columns);
  const [errors, setErrors] = useState<IErrorMessage>({ message: '' });

  const deletePermission =
    resourcePermissions &&
    resourcePermissions.resources &&
    checkResourcePermission(actions.DELETE, resourcePermissions.resources[resources.GROUPS]);

  const editPermission =
    resourcePermissions &&
    resourcePermissions.resources &&
    checkResourcePermission(actions.EDIT, resourcePermissions.resources[resources.GROUPS]);

  const getGroupsList = async (queryParams: any) => {
    showHideLoader(true);
    try {
      const res: any = await getAllUserGroupsRequest(queryParams);
      if (res && res.data) {
        dispatch(getAllUserGroupsAction(res));
      }
    } catch (error: any) {
      const errorMessage: any = {
        message: error.message,
        severity: 'error',
        showSnackbar: true
      };
      dispatch(setSnackBarMessage(errorMessage));
    }
    showHideLoader(false);
  };

  useEffect(() => {
    const queryParams = {
      pageSize: pageSize,
      pageNumber: page,
      searchValue: selectedSearchValue
    };
    getGroupsList(queryParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  const deleteUserGroup = async () => {
    showHideLoader(true);
    setDialogHideLoader(true);
    removeUserGroup(rowData);
  };

  const removeUserGroup = async (rowData: any) => {
    setErrors({ message: '' });
    try {
      await deleteUserGroupRequest(rowData.identifier);
      showHideLoader(false);
      setDialogHideLoader(false);
      setPreviewShown(false);
      dispatch(
        setSnackBarMessage({
          ...SNACKBAR_ALERT_DEFAULT_PROPS,
          severity: 'success',
          message: interpolateString([`${rowData.displayName}`], MESSAGES.USER_GROUP_DELETED)
        })
      );
      refreshOnDelete();
    } catch (error: any) {
      const errorMessage: any = error.response?.data?.message || error;
      showHideLoader(false);
      setDialogHideLoader(false);
      dispatch(
        setSnackBarMessage({
          ...SNACKBAR_ALERT_DEFAULT_PROPS,
          message: 'Error in removing User Group: ' + errorMessage,
          severity: 'error'
        })
      );
    }
  };
  const deleteOnClick = (rowData: any) => {
    setPreviewShown(true);
    setRowData(rowData);
  };

  const customRowActions: any = (row: MRT_Row<any>) => {
    return (
      <LuiActionsMenu
        items={[
          deletePermission
            ? {
                render: (
                  <IconButton aria-label="delete" data-testid="deleteIcon" onClick={() => deleteOnClick(row.original)}>
                    <Trash />
                  </IconButton>
                ),
                alwaysVisible: true
              }
            : []
        ].flat()}
        maxItemsToDisplay={2}
      />
    );
  };

  return (
    <Grid className={classes.card}>
        <>
          <LuiMaterialTable
            paging={false}
            loading={showLoader}
            columns={tableColumns}
            columnResizable={false}
            keyAccessor="identifier"
            enableColumnActions={false}
            enableBottomToolbar={false}
            data={selectAllUserGroupsList}
            enableAlternateRowStyle={true}
            paginationDisplayMode={'extended'}
            maxTableBodyHeight={height - 201}
            customRowActions={customRowActions}
            onRowClick={rowData => editPermission && onEdit(rowData)}
            renderTopToolbar={extendedToolbar}
            renderEmptyRowsFallback={emptyRowsFallback}
          />
          <LuiPagination
            rowsPerPage={pageSize}
            rowsPerPageOptions={[20, 50, 100]}
            totalRows={selectUserGroupPagination.totalCount}
            page={selectUserGroupPagination.pageNumber - 1}
            onPageChange={(_, newPage) => setPage(newPage + 1)}
            onRowsPerPageChange={(event: any) => setPageSize(event.target.value)}
            sx={{ padding: 0, width: 'auto' }}
          />
        </>
      {isPreviewShown && (
        <AlertDialog
          isStatement={false}
          hideCanNotBeUndoneMessage
          handleOnOk={deleteUserGroup}
          secondButtonText={LABELS.CANCEL}
          primaryButtonText={LABELS.DELETE}
          primaryButtonColor="error"
          showDialogLoader={showDialogLoader}
          handleOnCancel={() => setPreviewShown(false)}
          recordName={rowData?.displayName || ''}
          dialogTitle={LABELS.DELETE_CONFIRMATION}
          dialogContentText={LABELS.DELETE_USER_GROUP}
        />
      )}
      <SnackbarAlert />
      {errors && <ErrorMessage {...errors} />}
    </Grid>
  );
};

export default UserGroupsList;
